import { useFormik } from 'formik'
import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'
import { Form, FormItem } from 'sutra/components/Form'
import { Input } from 'sutra/components/Input/Input'
import { Heading } from 'sutra/components/Text/Heading'
import { Text } from 'sutra/components/Text/Text'
import * as Yup from 'yup'

import { handleException } from 'clients/exception-handling'
import { localStorageClient } from 'clients/local-storage'
import { Loader } from 'components'
import { AuthContext } from 'features/auth'
import { AuthWrapper } from 'features/auth/components/AuthWrapper'
import { useAuthService } from 'features/auth/service'
import { HOME } from 'features/projects/paths'
import { PROJECT_FILTERS_STORAGE_KEY } from 'features/projects/service/project-filters-storage'

const layout = {
  labelCol: { span: 6 },
}

const SignUpSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Enter valid '),
  first_name: Yup.string().required('Enter valid first name'),
  last_name: Yup.string().required('Enter valid last name'),
  business: Yup.string().required('Enter valid business name'),
  position: Yup.string().required('Enter valid position'),
})

export const SignUpTrialForm = (): JSX.Element => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [isSuccesfullyRegistered, setSuccesfullyRegistered] = useState(false)
  const authContext = useContext(AuthContext)
  const authService = useAuthService()
  const formik = useFormik({
    initialValues: {
      email: '',
      first_name: '',
      last_name: '',
      business: '',
      position: '',
    },
    validationSchema: SignUpSchema,
    onSubmit: async ({ email, first_name, last_name, business, position }) => {
      try {
        setLoading(true)

        localStorageClient.clearAllExcept([PROJECT_FILTERS_STORAGE_KEY])
        await authService.freeSignUp(
          email.toLowerCase(),
          first_name,
          last_name,
          business,
          position,
        )

        setSuccesfullyRegistered(true)
        toast.success(`Welcome, ${first_name}!`)
      } catch (e) {
        handleException(e, {
          operation: 'auth.trial.register',
          disposition: 'failed',
        })

        const err = e as {
          response?: { data?: { detail?: { msg?: string } } }
          message?: string
        }
        toast.error(err?.response?.data?.detail?.msg || err.message)
      } finally {
        setLoading(false)
      }
    },
  })

  window.$crisp?.push(['do', 'chat:show'])
  window.$crisp?.push([
    'on',
    'chat:closed',
    () => {
      window.$crisp?.push(['do', 'chat:show'])
    },
  ])

  useEffect(() => {
    if (authContext.isAuthenticated) {
      navigate(HOME)
    }
  }, [authContext.isAuthenticated, navigate])

  const emailError = formik.touched.email && formik.errors.email
  const firstNameError = formik.touched.first_name && formik.errors.first_name
  const lastNameError = formik.touched.last_name && formik.errors.last_name
  const businessError = formik.touched.business && formik.errors.business
  const positionError = formik.touched.position && formik.errors.position

  return (
    <AuthWrapper>
      <Wrapper layout="horizontal" onFinish={formik.submitForm} {...layout}>
        {!isSuccesfullyRegistered ? (
          <div>
            <FormTitle>
              <Heading as="h4" $level="5">
                Start using Yogi
              </Heading>
            </FormTitle>
            <FormItem
              label="Business Email"
              validateStatus={emailError ? 'error' : undefined}
              help={formik.touched.email && formik.errors.email}
            >
              <Input
                $error={!!emailError}
                name="email"
                autoComplete="email"
                onChange={formik.handleChange}
                value={formik.values.email}
              />
            </FormItem>
            <FormItem
              label="First name"
              validateStatus={firstNameError ? 'error' : undefined}
              help={formik.touched.first_name && formik.errors.first_name}
            >
              <Input
                $error={!!firstNameError}
                name="first_name"
                autoComplete="first_name"
                onChange={formik.handleChange}
                value={formik.values.first_name}
              />
            </FormItem>
            <FormItem
              label="Last name"
              validateStatus={lastNameError ? 'error' : undefined}
              help={formik.touched.last_name && formik.errors.last_name}
            >
              <Input
                $error={!!lastNameError}
                name="last_name"
                autoComplete="last_name"
                onChange={formik.handleChange}
                value={formik.values.last_name}
              />
            </FormItem>
            <FormItem
              label="Company"
              validateStatus={businessError ? 'error' : undefined}
              help={formik.touched.business && formik.errors.business}
            >
              <Input
                $error={!!businessError}
                name="business"
                autoComplete="business"
                onChange={formik.handleChange}
                value={formik.values.business}
              />
            </FormItem>
            <FormItem
              label="Your position"
              validateStatus={positionError ? 'error' : undefined}
              help={formik.touched.position && formik.errors.position}
            >
              <Input
                $error={!!positionError}
                name="position"
                autoComplete="position"
                onChange={formik.handleChange}
                value={formik.values.position}
              />
            </FormItem>
            {loading && (
              <Overlay>
                <Loader />
              </Overlay>
            )}
            <FormItem style={{ marginBottom: 0 }}>
              <SubmitButton variant="primary" type="submit">
                Submit
              </SubmitButton>
            </FormItem>
            <Line />
            <div style={{ textAlign: 'center' }}>
              <Text as="span" $size="md" $tone="inherit">
                {"Don't have deep insight into your reviews & ratings? "} <br />
                <a href="https://meetyogi.com/book-a-demo">
                  <b>Sign up for a demo!</b>
                </a>
              </Text>
            </div>
          </div>
        ) : (
          <div>
            <h2>Waiting for Email confirmation</h2>
            Please, check your email specified during registration. Soon you
            will receive mail with confirmation registration.
          </div>
        )}
      </Wrapper>
    </AuthWrapper>
  )
}

const Wrapper = styled(Form)`
  max-width: 500px;
  margin: 0 auto;
  background: #fff;
  box-shadow: var(--default-box-shadow);
  padding: var(--default-padding);
  position: relative;

  @media (max-width: 768px) {
    max-width: none;
    margin: var(--mobile-margin);
  }
`
const FormTitle = styled.div`
  margin-bottom: var(--default-padding);
  text-align: center;

  & > h4 {
    margin-bottom: 10px;
  }
`
const SubmitButton = styled(Button)`
  display: flex;
  width: fit-content;
  margin-left: auto;
  margin-right: auto;
  margin-top: var(--default-padding);
`

const Line = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(0, 0, 0, 0.6);
  margin: 30px 0;
`

const Overlay = styled.div`
  background: #fff;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
`
